import { Controller } from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';
import { installWorkers, loaded, themeName, watchTheme } from '../lib/monaco';

const SCHEME = 'flow';

const OPTIONS = {
  automaticLayout: true,
  fixedOverflowWidgets: true,
  fontFamily: 'SF Mono, Menlo, monospace',
  fontSize: 13,
  lineNumbers: 'off',
  folding: false,
  glyphMargin: false,
  lineDecorationsWidth: 8,
  minimap: { enabled: false },
  overviewRulerLanes: 0,
  padding: { top: 8, bottom: 8 },
  renderLineHighlight: 'none',
  scrollBeyondLastLine: false,
  scrollbar: { alwaysConsumeMouseWheel: false },
  tabSize: 2,
  wordWrap: 'on',
};

export default class extends Controller {
  static targets = ['source', 'host'];

  static values = { workers: Object, schema: Object, unknownOperator: String, rehearsalUrl: String };

  initialize() {
    this.ready = this.#setup();
  }

  disconnect() {
    this.ready.then(() => this.#teardown());
  }

  async rehearse() {
    const webhook = this.element.closest('[data-role="webhook-fields"]')?.querySelector('[data-field="webhook"]');

    if (!webhook?.value) return;

    const body = new FormData();

    body.append('conditions', this.sourceTarget.value);
    body.append('events', this.#events?.value ?? '');

    const response = await fetch(this.rehearsalUrlValue.replace(':webhook', webhook.value), {
      method: 'POST',
      body,
      headers: { Accept: 'text/vnd.turbo-stream.html', 'X-CSRF-Token': this.#token },
    });

    Turbo.renderStreamMessage(await response.text());
  }

  get #events() {
    return this.element.closest('[data-role="webhook-fields"]')?.querySelector('[data-field="events"]');
  }

  get #token() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? '';
  }

  async insert({ params }) {
    await this.ready;

    this.editor.focus();
    this.editor.trigger('badge', 'type', { text: `"${params.operator}": ` });
  }

  async #setup() {
    installWorkers(this.workersValue);

    const monaco = await loaded;

    this.#declareSchema(monaco);

    this.model = monaco.editor.createModel(
      this.sourceTarget.value,
      'json',
      monaco.Uri.parse(`${SCHEME}://conditions/${crypto.randomUUID()}.json`),
    );

    this.monaco = monaco;
    this.editor = monaco.editor.create(this.hostTarget, { ...OPTIONS, model: this.model, theme: themeName() });
    this.editor.onDidChangeModelContent(() => this.#publish());
    this.observer = watchTheme(monaco);

    this.#markUnknown();

    return monaco;
  }

  #publish() {
    this.sourceTarget.value = this.editor.getValue();
    this.sourceTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.#markUnknown();
  }

  #markUnknown() {
    const markers = this.#unknownOperators().flatMap((operator) => this.#markersFor(operator));

    this.monaco.editor.setModelMarkers(this.model, 'conditions', markers);
  }

  #markersFor(operator) {
    return this.model.findMatches(`"${operator}"`, false, false, true, null, false).map(({ range }) => ({
      ...range,
      severity: this.monaco.MarkerSeverity.Error,
      message: this.unknownOperatorValue.replace('%{operators}', operator),
    }));
  }

  #unknownOperators() {
    try {
      return [...new Set(this.#walk(JSON.parse(this.editor.getValue() || '{}')))];
    } catch {
      return [];
    }
  }

  #walk(node) {
    if (Array.isArray(node)) return node.flatMap((branch) => this.#walk(branch));
    if (node === null || typeof node !== 'object') return [];

    return Object.entries(node).flatMap(([key, value]) => {
      if (key.startsWith('$') && !this.#known.includes(key)) return [key];

      return this.#walk(value);
    });
  }

  get #known() {
    return Object.keys(this.schemaValue.definitions.condition.anyOf.at(-1).properties);
  }

  #declareSchema(monaco) {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      enableSchemaRequest: false,
      schemas: [
        {
          uri: `${SCHEME}://conditions/schema.json`,
          fileMatch: [`${SCHEME}://conditions/*.json`],
          schema: this.schemaValue,
        },
      ],
    });
  }

  #teardown() {
    this.observer.disconnect();
    this.editor.dispose();
    this.model.dispose();
  }
}
